import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { Download, Crown } from 'lucide-react'
import { LeaderboardEntry } from '@/types'
import html2canvas from 'html2canvas'
import { useRef, useState } from 'react'
import LeaderboardContainer from './LeaderboardContainer'
import { LeaderboardAvatarPreloader, LeaderboardLoadingIndicator } from './LeaderboardAvatarPreloader'

export default function TotalLeaderboard() {
  const leaderboardRef = useRef<HTMLDivElement>(null)
  const [avatarLoadProgress, setAvatarLoadProgress] = useState(0)

  const { data: leaderboard, isLoading } = useQuery({
    queryKey: ['leaderboard', 'all'],
    queryFn: () => api.getLeaderboard('all'),
    refetchInterval: 300000, // 每5分钟刷新一次
    staleTime: 1000 * 60 * 5, // 5分钟内不重新获取
    cacheTime: 1000 * 60 * 10, // 缓存10分钟
  })

  const handleDownload = async () => {
    if (!leaderboardRef.current) return

    try {
      // 添加html2canvas专用类
      leaderboardRef.current.classList.add('html2canvas')

      const canvas = await html2canvas(leaderboardRef.current, {
        useCORS: true,
        allowTaint: true,
        background: '#ffffff',
        logging: false // 关闭日志
      })

      // 移除html2canvas专用类
      leaderboardRef.current.classList.remove('html2canvas')

      const link = document.createElement('a')
      link.download = `总榜-${new Date().toISOString().split('T')[0]}.png`
      link.href = canvas.toDataURL('image/png', 1.0)
      link.click()
    } catch (error) {
      console.error('下载失败:', error)
      // 确保移除类，即使出错也要清理
      if (leaderboardRef.current) {
        leaderboardRef.current.classList.remove('html2canvas')
      }
      alert('下载失败，请稍后重试')
    }
  }



  if (isLoading) {
    return (
      <div className="bg-white/80 backdrop-blur-xl border border-gray-200/50 rounded-2xl shadow-xl p-4">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-3 text-gray-500 text-sm">加载总排行榜...</p>
        </div>
      </div>
    )
  }



  if (!leaderboard?.data || leaderboard.data.length === 0) {
    return (
      <div className="bg-white/80 backdrop-blur-xl border border-gray-200/50 rounded-2xl shadow-xl p-4">
        <div className="text-center py-8">
          <Crown className="mx-auto h-10 w-10 text-gray-400 mb-3" />
          <p className="text-gray-500 text-sm">暂无阅读记录</p>
        </div>
      </div>
    )
  }

  // 过滤出有阅读记录的用户
  const totalEntries = leaderboard.data.filter((entry: LeaderboardEntry) => entry.totalCount > 0)

  return (
    <div>


      {/* 头像预加载器 */}
      {totalEntries.length > 0 && (
        <LeaderboardAvatarPreloader
          entries={totalEntries}
          onProgress={setAvatarLoadProgress}
        />
      )}

      {/* 加载进度指示器 */}
      <LeaderboardLoadingIndicator
        progress={avatarLoadProgress}
        isVisible={totalEntries.length > 0 && avatarLoadProgress < 1}
      />

      <LeaderboardContainer
        ref={leaderboardRef}
        title="总排行榜"
        subtitle={`累计阅读成果统计 ${new Date().toLocaleDateString('zh-CN').replace(/\//g, '/')}`}
        entries={totalEntries}
        type="total"
        emptyMessage="暂无阅读记录"
      />

      {/* 总统计 */}
      {totalEntries.length > 0 && (
        <div className="p-4 bg-gray-50/50 border-t border-gray-100/50">
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <div className="text-lg font-semibold text-yellow-600">
                {totalEntries.length}
              </div>
              <div className="text-xs text-gray-500">总参与用户</div>
            </div>
            <div>
              <div className="text-lg font-semibold text-blue-600">
                {totalEntries.reduce((sum, entry) => sum + entry.totalCount, 0)}
              </div>
              <div className="text-xs text-gray-500">累计总遍数</div>
            </div>
            <div>
              <div className="text-lg font-semibold text-green-600">
                {Math.round(totalEntries.reduce((sum, entry) => sum + entry.totalCount, 0) / totalEntries.length * 10) / 10}
              </div>
              <div className="text-xs text-gray-500">人均遍数</div>
            </div>
          </div>
        </div>
      )}

      {/* 下载按钮 */}
      <div className="flex justify-center mt-4">
        <button
          onClick={handleDownload}
          className="flex items-center space-x-2 bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-600 hover:to-orange-700 text-white px-6 py-3 rounded-xl font-medium transition-all shadow-lg"
        >
          <Download className="h-4 w-4" />
          <span>下载图片</span>
        </button>
      </div>
    </div>
  )
}
